// The resolution transaction (§13, §14.1): a document, its bases and its includes become one resolved
// artifact (`instruction-resolved/1`) and its agent text (`instruction-text/1`).
//
//   1. read every source and keep its diagnostics;
//   2. compose depth-first: each base of `extends`, in declared order, is resolved on its own (its bases, patches
//      and includes first) as the instance `<parent>/<alias>`; the child's `replace`, `extend` and `remove` patches
//      then apply to that inherited tree, in source order, by qualified explicit id (`#alias/id`); its own body
//      follows, with each `::include` replaced by the content it selects, again a separate instance;
//   3. substitute each source's parameters into its parsed prose and its rules' conditions and reasons, as data;
//   4. emit the body with every node's instance, the dependencies and provenance, the diagnostics and a status, and
//      digest the whole in RFC 8785 form.
//
// The composition follows the draft's reference (tools/spec_support.py compose_fixture), with diagnostics where it
// raises. Loading is the host's: `resolve` asks `load` for each source by its resolved URI (the registry reads an
// instruction it may read, the CLI a file, the studio its workspace) and never fetches anything itself. Typed
// references resolve to the document's own objects; no resolver adapter is bound here, so nothing is enriched and a
// declared extension's manifest is never retrieved (an optional one leaves the result partial, a required one is an
// error).
import { sha256Hex } from "./digest";
import { inlineTokens } from "./inline";
import { jsonDigest } from "./jcs";
import { read, valueFits, type ReadResult } from "./read";
import { inlineDiagnostics } from "./check";
import { KINDS, RULES } from "./registry";
import { toText, valueText, type ReferenceRecord } from "./text";
import type { AuthoredDocument, Diagnostic, DirectiveNode, Identity, MarkdownNode, Node, Range, Severity } from "./types";

export type Selector =
  | { id: string }
  | { heading: string; occurrence?: number }
  | { lines: [number, number] }
  | { pointer: string }
  | { page: number }
  | { time: [number, number] }
  | { region: [number, number, number, number] };

/** A source a host hands the resolver: its resolved URI, its text and, when the provider knows one, its version. */
export interface Source { uri: string; text: string; version?: string }

/** Loads a source by its resolved URI (no fragment), or answers null when it is absent or the reader may not read it. */
export type Load = (uri: string) => Source | null | undefined | Promise<Source | null | undefined>;

export interface ResolveOptions {
  /** The document's own URI, which relative references resolve against (default `document.md`). */
  uri?: string;
  version?: string;
  /** Parameter values for the document (§12); a base gets its record's `with`. */
  params?: Record<string, unknown>;
  load?: Load;
  /** Composition depth (default 8) and the number of sources read (default 64): past either, an error, never a
   *  silently truncated result (§18). */
  limits?: { depth?: number; sources?: number };
}

export interface Dependency { uri: string; resolved_uri: string; sha256: string; mode: "live" | "pinned"; version?: string; selector?: Selector; instance: string }

export interface Provenance {
  instance: string;
  uri: string;
  range?: Range;
  selector?: Selector;
  operation?: "include" | "inherit" | "replace" | "extend" | "remove";
  target?: string;
  reason?: string;
  modified_by?: string;
  original?: unknown;
}

export interface ExtensionBinding { instance: string; alias: string; id: string; version: string; sha256: string }

/** `instruction-resolved/1` (schemas/resolved.schema.json). */
export interface Resolved {
  format: "instruction-resolved/1";
  status: "complete" | "partial";
  document: { uri: string; sha256: string; version?: string };
  body: Node[];
  extensions: ExtensionBinding[];
  objects: ReferenceRecord[];
  resources: ReferenceRecord[];
  dependencies: Dependency[];
  provenance: Provenance[];
  diagnostics: Diagnostic[];
  resolved_digest: string;
}

export interface Resolution {
  /** No error: a successful resolution, which a registry may deliver. */
  ok: boolean;
  resolved: Resolved;
  /** The agent text, `instruction-text/1`. */
  text: string;
  diagnostics: Diagnostic[];
  /** The document's own reading: its identities, parameters and lines. */
  root: ReadResult;
}

const DEFAULT_URI = "document.md";

/** Resolves a document: reads it, loads what it composes through `load` (in rounds, each source once), composes. */
export async function resolve(text: string, opts: ResolveOptions = {}): Promise<Resolution> {
  const uri = opts.uri ?? DEFAULT_URI;
  const max = opts.limits?.sources ?? 64;
  const sources = new Map<string, Source | null>();
  const pending = new Set(referencesOf(read(text, { uri }), uri));
  while (pending.size && opts.load) {
    const round = [...pending].filter((u) => !sources.has(u));
    pending.clear();
    if (!round.length) break;
    for (const u of round) {
      if (sources.size >= max) break;
      const s = (await opts.load(u)) ?? null;
      sources.set(u, s);
      if (s) for (const next of referencesOf(read(s.text, { uri: s.uri }), s.uri)) if (!sources.has(next)) pending.add(next);
    }
    if (sources.size >= max) break;
  }
  return resolveWith(text, sources, opts);
}

/** Resolves a document over sources already loaded (by resolved URI; null for one that could not be). */
export function resolveWith(text: string, sources: ReadonlyMap<string, Source | null>, opts: Omit<ResolveOptions, "load"> = {}): Resolution {
  const uri = opts.uri ?? DEFAULT_URI;
  const root: Loaded = { uri, sha256: `sha256:${sha256Hex(text)}`, ...(opts.version ? { version: opts.version } : {}), read: read(text, { uri }) };
  const lookup = (u: string): Loaded | null => {
    const s = sources.get(u);
    return s ? { uri: s.uri, sha256: `sha256:${sha256Hex(s.text)}`, ...(s.version ? { version: s.version } : {}), read: read(s.text, { uri: s.uri }) } : null;
  };
  return run(root, lookup, opts);
}

/** Resolves an authored tree over other trees (the draft's resolution vectors are trees). A tree has no source bytes,
 *  so its digest is that of its canonical JSON. */
export function resolveTree(document: AuthoredDocument, sources: ReadonlyMap<string, AuthoredDocument | null>, opts: Omit<ResolveOptions, "load"> = {}): Resolution {
  const asLoaded = (d: AuthoredDocument, u: string): Loaded => ({ uri: d.uri ?? u, sha256: jsonDigest(d), read: fromTree(d, d.uri ?? u) });
  const root = asLoaded(document, opts.uri ?? document.uri ?? DEFAULT_URI);
  return run(root, (u) => { const d = sources.get(u); return d ? asLoaded(d, u) : null; }, opts);
}

function run(root: Loaded, lookup: (uri: string) => Loaded | null, opts: Omit<ResolveOptions, "load">): Resolution {
  const composer = new Composer(lookup, opts);
  composer.diagnostics.push(...root.read.diagnostics, ...inlineDiagnostics(root.read));
  const body = composer.compose(root, [], "root", opts.params ?? {}) ?? [];
  const diagnostics = sortDiagnostics(composer.diagnostics);
  const partial = composer.partial || diagnostics.some((d) => d.code === "reference-optional" || d.code === "parameter-optional");
  const artifact: Omit<Resolved, "resolved_digest"> = {
    format: "instruction-resolved/1",
    status: partial ? "partial" : "complete",
    document: { uri: root.uri, sha256: root.sha256, ...(root.version ? { version: root.version } : {}) },
    body,
    extensions: [],
    objects: [],
    resources: [],
    dependencies: composer.dependencies,
    provenance: composer.provenance,
    diagnostics,
  };
  const resolved: Resolved = { ...artifact, resolved_digest: jsonDigest(artifact) };
  const known = (instance: string, id: string) => !!composer.identities.get(instance)?.has(id);
  return { ok: !diagnostics.some((d) => d.severity === "error"), resolved, text: toText(resolved, { known }), diagnostics, root: root.read };
}

/** A tree as the composer reads a source: its explicit identities and every Markdown node outside a literal body as
 *  prose. Its declarations are not read: a tree's parameters stay as written (the draft's resolution vectors, the
 *  trees resolved here, declare none). */
function fromTree(document: AuthoredDocument, uri: string): ReadResult {
  const identities = new Map<string, Identity>();
  const prose = new Set<MarkdownNode>();
  const walk = (nodes: Node[], literal: boolean) => {
    for (const n of nodes) {
      if (n.type === "markdown") { if (!literal) prose.add(n); continue; }
      if (typeof n.attrs.id === "string") identities.set(n.attrs.id, { id: n.attrs.id, kind: n.kind, line: n.range?.start ?? 0 });
      walk(n.children ?? [], literal || n.kind === "example" || n.attrs.verbatim === true || !KINDS[n.kind]);
    }
  };
  walk(document.body, false);
  const doc: AuthoredDocument = { ...document, uri, metadata: { front: document.metadata?.front ?? {}, back: document.metadata?.back ?? {} } };
  return { document: doc, diagnostics: [], identities, parameters: new Map(), footnotes: new Map(), prose, keywords: new Map(), reasons: new Map(), comments: new Set(), lines: [], frontRange: null, backRange: null };
}

/** The sources a document composes, as resolved URIs without fragments: its bases and its includes. */
export function referencesOf(r: ReadResult, base: string): string[] {
  const out: string[] = [];
  const extendsList = r.document.metadata.front.extends;
  if (Array.isArray(extendsList)) for (const e of extendsList) if (e && typeof e === "object" && typeof (e as Record<string, unknown>).uri === "string") out.push(withoutFragment(resolveUri(named(r, String((e as Record<string, unknown>).uri)), base)));
  const walk = (nodes: Node[]) => {
    for (const n of nodes) {
      if (n.type !== "directive") continue;
      if (n.kind === "include" && typeof n.attrs.src === "string") out.push(withoutFragment(resolveUri(named(r, n.attrs.src), base)));
      if (n.children) walk(n.children);
    }
  };
  walk(r.document.body);
  return [...new Set(out)];
}

// ---- composition ----

interface Loaded { uri: string; sha256: string; version?: string; read: ReadResult }

/** A base record or an include, as the composer imports either. */
interface Import { uri: string; as: string; mode?: unknown; sha256?: unknown; version?: unknown; select?: unknown; required: boolean; with?: Record<string, unknown> }

class Composer {
  readonly diagnostics: Diagnostic[] = [];
  readonly provenance: Provenance[] = [];
  readonly dependencies: Dependency[] = [];
  partial = false;
  /** Each instance's explicit identities: what a typed reference written in it can name. */
  readonly identities = new Map<string, ReadonlyMap<string, unknown>>();
  private readonly loaded = new Map<string, Loaded>();
  private readonly depth: number;

  constructor(private readonly lookup: (uri: string) => Loaded | null, opts: Omit<ResolveOptions, "load">) {
    this.depth = opts.limits?.depth ?? 8;
  }

  private diag(code: string, severity: Severity, message: string, at?: { uri?: string; line?: number }) {
    const d: Diagnostic = { code, severity, message };
    if (at?.uri) d.uri = at.uri;
    if (at?.line) d.range = { start: at.line, end: at.line };
    this.diagnostics.push(d);
  }

  /** A document's effective body as the instance `instance`, or null when it cannot be composed (a cycle, the depth). */
  compose(doc: Loaded, trail: string[], instance: string, values: Record<string, unknown>): Node[] | null {
    const r = doc.read;
    const at = (line?: number) => ({ uri: instance === "root" ? undefined : doc.uri, line });
    if (trail.includes(doc.uri)) { this.diag("composition-cycle", "error", `${doc.uri} composes itself: ${[...trail, doc.uri].join(" → ")}`, at()); return null; }
    if (trail.length >= this.depth) { this.diag("limit-exceeded", "error", `composition is deeper than ${this.depth} documents at ${doc.uri}`, at()); return null; }
    this.loaded.set(doc.uri, this.loaded.get(doc.uri) ?? doc);
    this.identities.set(instance, r.identities);
    const scope = this.parameters(doc, instance, values);
    this.extensions(doc, instance);
    const aliases = new Set<string>();
    const inherited: Node[] = [];
    const bases = r.document.metadata.front.extends;
    if (bases !== undefined && !Array.isArray(bases)) this.diag("invalid-metadata", "error", "extends is a list of base records: { uri, as, mode, sha256, version, select, with }", at(r.frontRange?.start));
    for (const b of Array.isArray(bases) ? bases : []) {
      const rec = b && typeof b === "object" ? (b as Record<string, unknown>) : {};
      if (typeof rec.uri !== "string" || typeof rec.as !== "string" || !rec.as) {
        this.diag("invalid-metadata", "error", "each base in extends has a uri and an alias (as)", at(r.frontRange?.start));
        continue;
      }
      if (aliases.has(rec.as)) { this.diag("duplicate-id", "error", `the alias "${rec.as}" is used twice in ${doc.uri}`, at(r.frontRange?.start)); continue; }
      aliases.add(rec.as);
      const withValues = rec.with && typeof rec.with === "object" && !Array.isArray(rec.with) ? (rec.with as Record<string, unknown>) : {};
      const nodes = this.import(doc, trail, instance, { uri: rec.uri, as: rec.as, mode: rec.mode, sha256: rec.sha256, version: rec.version, select: rec.select, required: true, with: withValues }, "inherit", r.frontRange?.start);
      if (nodes) inherited.push(...nodes);
    }
    const local: Node[] = [];
    for (const node of r.document.body) {
      if (node.type === "directive" && (node.kind === "replace" || node.kind === "extend" || node.kind === "remove")) {
        this.patch(doc, instance, inherited, node, scope);
        continue;
      }
      local.push(this.stamp(node, instance, scope));
    }
    return [...inherited, ...this.includes(doc, trail, instance, local, aliases)];
  }

  /** Each `::include` in `nodes` (at any depth) replaced by what it selects; one that cannot be resolved stays. */
  private includes(doc: Loaded, trail: string[], instance: string, nodes: Node[], aliases: Set<string>): Node[] {
    const out: Node[] = [];
    for (const n of nodes) {
      if (n.type === "directive" && n.kind === "include") {
        const a = n.attrs;
        const alias = typeof a.as === "string" ? a.as : "";
        if (typeof a.src !== "string" || !alias) { out.push(n); continue; } // the reader reported it
        if (aliases.has(alias)) { this.diag("duplicate-id", "error", `the alias "${alias}" is used twice in ${doc.uri}`, { uri: instance === "root" ? undefined : doc.uri, line: n.range?.start }); out.push(n); continue; }
        aliases.add(alias);
        const nodes = this.import(doc, trail, instance, { uri: a.src, as: alias, mode: a.mode, sha256: a.sha256, version: a.version, required: a.required === true }, "include", n.range?.start);
        if (nodes) out.push(...nodes);
        else out.push(n);
        continue;
      }
      if (n.type === "directive" && n.children) n.children = this.includes(doc, trail, instance, n.children, aliases);
      out.push(n);
    }
    return out;
  }

  /** A base or an include: loaded, checked against its pin, composed as `<instance>/<alias>` and selected. */
  private import(doc: Loaded, trail: string[], instance: string, ref: Import, operation: "inherit" | "include", line?: number): Node[] | null {
    const at = { uri: instance === "root" ? undefined : doc.uri, line };
    const what = operation === "inherit" ? `the base "${ref.as}"` : `the include "${ref.as}"`;
    const absolute = resolveUri(named(doc.read, ref.uri), doc.uri);
    const [path, fragment] = splitFragment(absolute);
    const missing = (message: string) => {
      if (ref.required) this.diag("reference-missing", "error", message, at);
      else { this.diag("reference-optional", "warning", message, at); this.partial = true; }
      return null;
    };
    const target = this.source(path);
    if (!target) return missing(`${what}: ${ref.uri} could not be read`);
    const mode = ref.mode === "pinned" ? "pinned" : "live";
    if (mode === "pinned" && ref.sha256 !== target.sha256) {
      this.diag("pin-mismatch", "error", `${what} is pinned to ${typeof ref.sha256 === "string" ? ref.sha256 : "no digest"}, and ${ref.uri} is ${target.sha256}`, at);
      return null;
    }
    if (typeof ref.version === "string" && target.version !== undefined && target.version !== ref.version) {
      this.diag("pin-mismatch", "error", `${what} asks for version ${ref.version}, and ${ref.uri} is ${target.version}`, at);
      return null;
    }
    const child = `${instance}/${ref.as}`;
    const selector = selectorOf(ref.select, fragment);
    if (selector === "invalid") { this.diag("invalid-attributes", "error", `${what}: a selector is one of id, heading or lines for an instruction document`, at); return null; }
    let body: Node[] | null;
    if (selector && "lines" in selector) {
      const [a, b] = selector.lines;
      const lines = target.read.lines;
      if (!(a >= 1 && b >= a && b <= lines.length)) return missing(`${what}: lines ${a}–${b} are outside ${ref.uri}`);
      const fragmentRead = read(lines.slice(a - 1, b).join("\n") + "\n", { fragment: true, uri: target.uri });
      body = this.compose({ ...target, read: fragmentRead }, [...trail, doc.uri], child, ref.with ?? {});
    } else {
      body = this.compose(target, [...trail, doc.uri], child, ref.with ?? {});
      if (body && selector) body = select(body, child, selector);
      if (body === null && selector) return missing(`${what}: ${ref.uri} has nothing that ${describe(selector)}`);
    }
    if (!body) return null;
    this.dependencies.push({ uri: ref.uri, resolved_uri: path, sha256: target.sha256, mode, ...(target.version ? { version: target.version } : {}), ...(selector ? { selector } : {}), instance: child });
    this.provenance.push({ instance: child, uri: target.uri, ...(selector ? { selector } : {}), operation });
    return body;
  }

  /** A patch (§13.2) applied to the inherited tree: by qualified explicit id, against the tree as it is now. */
  private patch(doc: Loaded, instance: string, inherited: Node[], node: DirectiveNode, scope: Scope) {
    const at = { uri: instance === "root" ? undefined : doc.uri, line: node.range?.start };
    const raw = typeof node.attrs.target === "string" ? node.attrs.target : "";
    const target = raw.replace(/^#/, "");
    const parts = target.split("/");
    if (!raw.startsWith("#") || parts.length < 2 || parts.some((p) => !p)) {
      this.diag("patch-target", "error", `:::${node.kind}: its target is an inherited explicit id, #alias/id (it is "${raw}")`, at);
      return;
    }
    const wanted = `${instance}/${parts.slice(0, -1).join("/")}`;
    const id = parts[parts.length - 1];
    const found = findId(inherited, wanted, id);
    if (!found) { this.diag("patch-target", "error", `:::${node.kind}: nothing inherited has the id #${target}`, at); return; }
    const [siblings, index] = found;
    const dest = siblings[index] as DirectiveNode;
    const original = structuredClone(dest);
    const op = node.kind as "replace" | "extend" | "remove";
    if (op === "remove") siblings.splice(index, 1);
    else if (op === "replace") {
      if (original.type === "directive" && original.value) {
        if (!node.value || node.value.format !== original.value.format) {
          this.diag("patch-body", "error", `:::replace{target="#${target}"}: the target is a ${original.value.format.toUpperCase()} body, so its replacement is one too (format=${original.value.format})`, at);
          return;
        }
        dest.value = structuredClone(node.value);
        delete dest.children;
      } else {
        if (node.value) { this.diag("patch-body", "error", `:::replace{target="#${target}"}: the target is Markdown, so its replacement is Markdown`, at); return; }
        const children = (node.children ?? []).map((c) => this.stamp(c, instance, scope));
        const properties = children.filter((c): c is DirectiveNode => c.type === "directive" && c.kind === "properties" && c.attrs.patch === true);
        for (const p of properties) if (!this.properties(dest, p, target, at)) return;
        dest.children = children.filter((c) => !properties.includes(c as DirectiveNode));
      }
    } else {
      if (original.type === "directive" && original.value) {
        this.diag("patch-body", "error", `:::extend{target="#${target}"}: a structured target is changed with :::replace`, at);
        return;
      }
      const children = (node.children ?? []).map((c) => this.stamp(c, instance, scope));
      dest.children = node.attrs.position === "prepend" ? [...children, ...(dest.children ?? [])] : [...(dest.children ?? []), ...children];
    }
    const reason = typeof node.attrs.reason === "string" && node.attrs.reason ? node.attrs.reason : undefined;
    this.provenance.push({ instance: wanted, uri: doc.uri, ...(node.range ? { range: node.range } : {}), operation: op, target, ...(reason ? { reason } : {}), modified_by: doc.uri, original });
  }

  /** A `:::properties{patch=true}` inside a replacement: its mapping overrides the target's attributes, never its id. */
  private properties(dest: DirectiveNode, p: DirectiveNode, target: string, at: { uri?: string; line?: number }): boolean {
    const data = p.value?.data;
    if (!data || typeof data !== "object" || Array.isArray(data)) { this.diag("patch-body", "error", `the properties patch for #${target} is a mapping of attributes`, at); return false; }
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (k === "id") { this.diag("patch-body", "error", `a properties patch cannot change #${target}'s id`, at); return false; }
      dest.attrs[k] = v as DirectiveNode["attrs"][string];
    }
    return true;
  }

  /** The source at a resolved URI, read once; its own problems are reported once, under its URI. */
  private source(uri: string): Loaded | null {
    const known = this.loaded.get(uri);
    if (known) return known;
    const l = this.lookup(uri);
    if (!l) return null;
    for (const d of [...l.read.diagnostics, ...inlineDiagnostics(l.read)]) this.diagnostics.push({ ...d, uri: l.uri });
    this.loaded.set(uri, l);
    return l;
  }

  // ---- parameters (§12) ----

  /** A source's parameter values: supplied ones checked against their declarations, defaults for the rest. */
  private parameters(doc: Loaded, instance: string, values: Record<string, unknown>): Scope {
    const out = new Map<string, unknown>();
    const where = (line: number | "front-matter") => ({ uri: instance === "root" ? undefined : doc.uri, line: line === "front-matter" ? doc.read.frontRange?.start : line });
    for (const [name, p] of doc.read.parameters) {
      const given = Object.prototype.hasOwnProperty.call(values, name) ? values[name] : undefined;
      if (given !== undefined) {
        if (!valueFits(p, given)) { this.diag("parameter-invalid", "error", `the value given for \${${name}} is not a valid ${p.type}${p.values ? ` (${p.values.join(", ")})` : ""}`, where(p.declaredAt)); continue; }
        out.set(name, given);
      } else if (p.default !== undefined) out.set(name, p.default);
      else if (p.required) this.diag("parameter-missing", "error", `\${${name}} is required and has no value`, where(p.declaredAt));
      else if (usesParameter(doc.read, name)) {
        this.diag("parameter-optional", "warning", `\${${name}} has no value, so it stays as written`, where(p.declaredAt));
      }
    }
    return { values: out, prose: doc.read.prose };
  }

  /** Declared extensions: no manifest is retrieved here, so an optional binding leaves the result partial and a
   *  required one is an error (§15). */
  private extensions(doc: Loaded, instance: string) {
    const ext = doc.read.document.metadata.front.extensions;
    if (!ext || typeof ext !== "object") return;
    for (const [alias, binding] of Object.entries(ext as Record<string, unknown>)) {
      const required = !!binding && typeof binding === "object" && (binding as Record<string, unknown>).required === true;
      const id = binding && typeof binding === "object" ? String((binding as Record<string, unknown>).id ?? alias) : alias;
      if (required) this.diag("extension-unavailable", "error", `the extension ${id} ("${alias}") is required, and this resolver binds no extension manifests`, { uri: instance === "root" ? undefined : doc.uri, line: doc.read.frontRange?.start });
      else this.partial = true;
    }
  }

  /** A copy of a node as instance `instance`, its prose and its rules' conditions and reasons with values substituted. */
  private stamp(node: Node, instance: string, scope: Scope): Node {
    if (node.type === "markdown") {
      const copy: MarkdownNode = { ...node, instance };
      if (scope.prose.has(node)) copy.source = substitute(node.source, scope.values);
      return copy;
    }
    const copy: DirectiveNode = { ...node, attrs: { ...node.attrs }, instance };
    if (node.value) copy.value = structuredClone(node.value);
    if (RULES.has(node.kind)) {
      for (const k of ["if", "because"] as const) if (typeof copy.attrs[k] === "string") copy.attrs[k] = interpolate(copy.attrs[k] as string, scope.values);
    }
    if (node.children) copy.children = node.children.map((c) => this.stamp(c, instance, scope));
    return copy;
  }
}

interface Scope { values: Map<string, unknown>; prose: Set<MarkdownNode> }

/** `${name}` in parsed prose (the inline tokenizer's, so never in code, math, URLs or attributes), as data. */
function substitute(source: string, values: Map<string, unknown>): string {
  let out = "";
  let at = 0;
  for (const t of inlineTokens(source)) {
    if (t.type !== "param" || t.start < at) continue;
    const v = values.has(t.name) ? valueText(values.get(t.name)) : null;
    if (v === null) continue;
    out += source.slice(at, t.start) + v;
    at = t.end;
  }
  return out + source.slice(at);
}

function interpolate(s: string, values: Map<string, unknown>): string {
  return s.replace(/\$\{([A-Za-z_][A-Za-z0-9_.-]*)\}/g, (m, name: string) => (values.has(name) ? valueText(values.get(name)) ?? m : m));
}

function usesParameter(r: ReadResult, name: string): boolean {
  const ruleUses = (nodes: Node[]): boolean => nodes.some((n) => n.type === "directive" && ((RULES.has(n.kind) && [n.attrs.if, n.attrs.because].some((v) => typeof v === "string" && v.includes(`\${${name}}`))) || ruleUses(n.children ?? [])));
  return [...r.prose].some((n) => inlineTokens(n.source).some((t) => t.type === "param" && t.name === name)) || ruleUses(r.document.body);
}

// ---- selection (§11.2) ----

function selectorOf(select: unknown, fragment: string | null): Selector | null | "invalid" {
  if (select && typeof select === "object" && !Array.isArray(select)) {
    const s = select as Record<string, unknown>;
    const keys = Object.keys(s);
    if (typeof s.id === "string" && keys.length === 1) return { id: s.id };
    if (typeof s.heading === "string" && keys.every((k) => k === "heading" || k === "occurrence")) return { heading: s.heading, ...(typeof s.occurrence === "number" ? { occurrence: s.occurrence } : {}) };
    if (Array.isArray(s.lines) && s.lines.length === 2 && s.lines.every((x) => Number.isInteger(x)) && keys.length === 1) return { lines: [s.lines[0] as number, s.lines[1] as number] };
    return "invalid";
  }
  if (select !== undefined) return "invalid";
  if (fragment === null || fragment === "") return null;
  // (each run of escapes decoded where it can be: a malformed one stays as written, and names nothing)
  const f = fragment.replace(/(?:%[0-9A-Fa-f]{2})+/g, (run) => { try { return decodeURIComponent(run); } catch { return run; } });
  if (f.startsWith("id=")) return { id: f.slice(3) };
  if (f.startsWith("heading=")) return { heading: f.slice(8) };
  // an ordinary fragment: an explicit id first, then a generated heading anchor
  return { id: f };
}

const describe = (s: Selector) => ("id" in s ? `has the id ${s.id}` : "heading" in s ? `is the heading "${s.heading}"` : "matches the selector");

/** The selected part of a composed body, or null. An id selects that node and keeps the policies around it (the
 *  enclosing policy context, §13.1); a heading selects its section of the top-level Markdown. */
function select(body: Node[], instance: string, s: Selector): Node[] | null {
  if ("id" in s) return selectId(body, instance, s.id) ?? selectHeading(body, (h) => h.id === s.id || h.anchor === s.id, 1);
  if ("heading" in s) return selectHeading(body, (h) => h.text === s.heading, s.occurrence ?? 1);
  return null;
}

function selectId(nodes: Node[], instance: string, id: string): Node[] | null {
  for (const n of nodes) {
    if (n.type !== "directive") continue;
    if (n.attrs.id === id && (n.instance ?? instance) === instance) return [n];
    const found = selectId(n.children ?? [], instance, id);
    if (found) return n.kind === "policy" ? [{ ...n, children: found }] : found;
  }
  return null;
}

interface Heading { level: number; text: string; id?: string; anchor: string; node: number; line: number }

const ATX = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;

/** A section of the top-level Markdown: from its heading to the next heading of the same level or higher. */
function selectHeading(body: Node[], match: (h: Heading) => boolean, occurrence: number): Node[] | null {
  const headings: Heading[] = [];
  const anchors = new Map<string, number>();
  body.forEach((n, i) => {
    if (n.type !== "markdown") return;
    let code = false;
    n.source.split("\n").forEach((l, li) => {
      if (/^ {0,3}(`{3,}|~{3,})/.test(l)) code = !code;
      if (code) return;
      const m = ATX.exec(l);
      if (!m) return;
      const attrs = /[ \t]\{([^{}]*)\}$/.exec(m[2]);
      const visible = attrs ? m[2].slice(0, attrs.index).trim() : m[2];
      const id = attrs ? /(?:^|\s)#([A-Za-z0-9][A-Za-z0-9._-]*)/.exec(attrs[1])?.[1] : undefined;
      const text = plain(visible);
      let anchor = slug(text);
      const seen = anchors.get(anchor) ?? 0;
      anchors.set(anchor, seen + 1);
      if (seen) anchor = `${anchor}-${seen + 1}`;
      headings.push({ level: m[1].length, text, ...(id ? { id } : {}), anchor, node: i, line: li });
    });
  });
  const hit = headings.filter(match)[occurrence - 1];
  if (!hit) return null;
  const next = headings.find((h) => (h.node > hit.node || (h.node === hit.node && h.line > hit.line)) && h.level <= hit.level);
  const out: Node[] = [];
  for (let i = hit.node; i < body.length; i++) {
    const n = body[i];
    if (next && i > next.node) break;
    if (n.type !== "markdown") { if (!next || i < next.node) out.push(n); continue; }
    const lines = n.source.split("\n");
    const from = i === hit.node ? hit.line : 0;
    const to = next && i === next.node ? next.line : lines.length;
    const part = lines.slice(from, to).join("\n");
    if (part.trim()) out.push({ ...n, source: part.endsWith("\n") ? part : `${part}\n` });
  }
  return out;
}

/** A heading's visible text: inline code, emphasis and links reduced to their text. */
function plain(s: string): string {
  return s.replace(/`([^`]*)`/g, "$1").replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/[*_~]+/g, "").trim();
}

/** The generated anchor (§11.2): casefolded, whitespace runs to `-`, only letters, numbers, `_` and `-`, trimmed. */
export function slug(text: string): string {
  const s = text.toLowerCase().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}_-]/gu, "").replace(/^-+|-+$/g, "");
  return s || "section";
}

function findId(nodes: Node[], instance: string, id: string): [Node[], number] | null {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (n.type !== "directive") continue;
    if (n.instance === instance && n.attrs.id === id) return [nodes, i];
    const found = findId(n.children ?? [], instance, id);
    if (found) return found;
  }
  return null;
}

// ---- URIs ----

/** A named reference's URI (front matter `references`), else the target itself. */
function named(r: ReadResult, target: string): string {
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(target) || target.startsWith(".") || target.startsWith("/") || target.includes("/") || target.includes("#")) return target;
  const refs = r.document.metadata.front.references;
  const rec = refs && typeof refs === "object" ? (refs as Record<string, unknown>)[target] : undefined;
  if (typeof rec === "string") return rec;
  if (rec && typeof rec === "object" && typeof (rec as Record<string, unknown>).uri === "string") return String((rec as Record<string, unknown>).uri);
  return target;
}

/** RFC 3986 reference resolution against `base`: a URI with a scheme stays; a relative one is merged with the base's
 *  path (a base without a scheme, `docs/child.md`, resolves as a path). */
export function resolveUri(ref: string, base: string): string {
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(ref)) return ref;
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(base)) {
    try { return new URL(ref, base).href; } catch { return ref; }
  }
  const [path, fragment] = splitFragment(ref);
  const joined = path === "" ? withoutFragment(base) : path.startsWith("/") ? path : `${withoutFragment(base).replace(/[^/]*$/, "")}${path}`;
  const segments: string[] = [];
  for (const s of joined.split("/")) {
    if (s === "." || (s === "" && segments.length)) continue;
    if (s === "..") { if (segments.length && segments[segments.length - 1] !== "") segments.pop(); continue; }
    segments.push(s);
  }
  return segments.join("/") + (fragment !== null ? `#${fragment}` : "");
}

function splitFragment(uri: string): [string, string | null] {
  const i = uri.indexOf("#");
  return i < 0 ? [uri, null] : [uri.slice(0, i), uri.slice(i + 1)];
}

const withoutFragment = (uri: string) => splitFragment(uri)[0];

/** §18: by source URI, start line, then code. */
function sortDiagnostics(ds: Diagnostic[]): Diagnostic[] {
  const cmp = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
  return [...ds].sort((a, b) => cmp(a.uri ?? "", b.uri ?? "") || (a.range?.start ?? 0) - (b.range?.start ?? 0) || cmp(a.code, b.code));
}
